import React, { useState, useEffect } from "react";
import {
  User,
  Phone,
  MapPin,
  Send,
  Loader2,
  Camera,
  FileText,
  CheckCircle2,
  AlertCircle,
  X,
} from "lucide-react";

const EntryForm = ({ onSave, initialData }) => {
  const [loading, setLoading] = useState(false);
  const [location, setLocation] = useState({
    lat: null,
    lng: null,
    error: null,
  });
  const [photoPreview, setPhotoPreview] = useState(null);
  const [formData, setFormData] = useState({
    customerName: "",
    phoneNumber: "",
    visitType: "marketing",
    remarks: "",
    photo: null,
  });

  // Sync state if initialData is provided (for editing)
  useEffect(() => {
    if (initialData) {
      setFormData(initialData);
    }
  }, [initialData]);

  // Capture Location on Mount
  useEffect(() => {
    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setLocation({
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
            error: null,
          });
        },
        (err) =>
          setLocation((prev) => ({
            ...prev,
            error: "Location required for check-in",
          })),
        { enableHighAccuracy: true },
      );
    }
  }, []);

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData({ ...formData, photo: file });
      setPhotoPreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!location.lat) {
      alert("Please wait for GPS verification before submitting.");
      return;
    }
    setLoading(true);

    // Simulating API call
    setTimeout(() => {
      onSave({
        ...formData,
        lat: location.lat,
        lng: location.lng,
        id: initialData?.id || Date.now(),
        date: initialData?.date || new Date().toLocaleDateString(),
      });
      setLoading(false);
    }, 1000);
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="grid grid-cols-1 lg:grid-cols-3 gap-8 animate-in fade-in slide-in-from-bottom-4"
    >
      {/* LEFT: Main Information */}
      <div className="lg:col-span-2 bg-white p-6 md:p-8 rounded-3xl border border-slate-200 shadow-sm space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <label className="text-sm font-bold text-slate-700 flex items-center gap-2">
              <User className="w-4 h-4 text-blue-600" /> Customer Name
            </label>
            <input
              required
              placeholder="Full business name"
              className="w-full p-4 bg-slate-50 border border-slate-200 rounded-2xl outline-none focus:ring-2 focus:ring-blue-500 transition-all"
              value={formData.customerName}
              onChange={(e) =>
                setFormData({ ...formData, customerName: e.target.value })
              }
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-bold text-slate-700 flex items-center gap-2">
              <Phone className="w-4 h-4 text-blue-600" /> Phone Number
            </label>
            <input
              required
              type="tel"
              placeholder="Contact number"
              className="w-full p-4 bg-slate-50 border border-slate-200 rounded-2xl outline-none focus:ring-2 focus:ring-blue-500 transition-all"
              value={formData.phoneNumber}
              onChange={(e) =>
                setFormData({ ...formData, phoneNumber: e.target.value })
              }
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <label className="text-sm font-bold text-slate-700">
              Purpose of Visit
            </label>
            <select
              className="w-full p-4 bg-slate-50 border border-slate-200 rounded-2xl outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer"
              value={formData.visitType}
              onChange={(e) =>
                setFormData({ ...formData, visitType: e.target.value })
              }
            >
              <option value="marketing">Marketing (Default)</option>
              <option value="routine">Routine Check-in</option>
              <option value="collection">Payment Collection</option>
              <option value="complaint">Complaint Resolution</option>
            </select>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-bold text-slate-700">
              GPS Verification
            </label>
            <div
              className={`flex items-center gap-3 p-4 rounded-2xl border transition-colors ${
                location.lat
                  ? "bg-emerald-50 border-emerald-200 text-emerald-700"
                  : "bg-amber-50 border-amber-200 text-amber-700"
              }`}
            >
              {location.lat ? (
                <CheckCircle2 className="w-5 h-5 text-emerald-600" />
              ) : (
                <Loader2 className="w-5 h-5 animate-spin text-amber-600" />
              )}
              <div className="flex flex-col overflow-hidden">
                <span className="text-[10px] uppercase font-black tracking-widest leading-none">
                  Status
                </span>
                <span className="text-xs font-bold truncate">
                  {location.lat
                    ? `${location.lat.toFixed(4)}, ${location.lng.toFixed(4)}`
                    : location.error || "Acquiring Signal..."}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-2">
          <label className="text-sm font-bold text-slate-700 flex items-center gap-2">
            <FileText className="w-4 h-4 text-blue-600" /> Visit Remarks
          </label>
          <textarea
            required
            className="w-full p-4 bg-slate-50 border border-slate-200 rounded-2xl outline-none focus:ring-2 focus:ring-blue-500 transition-all resize-none"
            rows="4"
            placeholder="What was discussed during the visit?"
            value={formData.remarks}
            onChange={(e) =>
              setFormData({ ...formData, remarks: e.target.value })
            }
          />
        </div>
      </div>

      {/* RIGHT: Image Evidence & Action */}
      <div className="space-y-6">
        <div className="bg-white p-6 rounded-3xl border border-slate-200 shadow-sm">
          <label className="text-sm font-bold text-slate-700 flex items-center gap-2 mb-4">
            <Camera className="w-4 h-4 text-blue-600" /> Visit Evidence
          </label>

          <div className="relative group">
            <div
              className={`w-full h-52 rounded-2xl border-2 border-dashed flex flex-col items-center justify-center overflow-hidden transition-all bg-slate-50 ${
                !photoPreview
                  ? "border-slate-200 hover:border-blue-300 hover:bg-slate-100"
                  : "border-transparent"
              }`}
            >
              {photoPreview ? (
                <div className="relative w-full h-full">
                  <img
                    src={photoPreview}
                    alt="Preview"
                    className="w-full h-full object-cover"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      setPhotoPreview(null);
                      setFormData({ ...formData, photo: null });
                    }}
                    className="absolute top-2 right-2 bg-white/80 p-1.5 rounded-full text-red-500 hover:bg-white"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <div className="text-center p-4">
                  <Camera className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                  <p className="text-xs text-slate-500 font-medium">
                    Click to capture shop front
                  </p>
                </div>
              )}
              <input
                type="file"
                accept="image/*"
                capture="environment"
                className="absolute inset-0 opacity-0 cursor-pointer"
                onChange={handlePhotoChange}
              />
            </div>
          </div>

          <div className="mt-8 space-y-4">
            <button
              type="submit"
              disabled={loading || !location.lat}
              className="w-full flex items-center justify-center gap-3 bg-blue-600 hover:bg-blue-700 text-white font-bold py-4 rounded-2xl shadow-lg shadow-blue-100 transition-all active:scale-95 disabled:opacity-50"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <Send className="w-5 h-5" />{" "}
                  {initialData ? "Update Visit Log" : "Submit Visit Log"}
                </>
              )}
            </button>
            <p className="text-[10px] text-center text-slate-400 font-medium leading-relaxed">
              GPS coordinates and timestamp are automatically captured for audit
              purposes.
            </p>
          </div>
        </div>
      </div>
    </form>
  );
};

export default EntryForm;
